import { SidebarInset, SidebarProvider, SidebarTrigger } from '@repo/ui/components/ui/sidebar'
import { type ReactNode, useMemo } from 'react'
import { AppSidebar } from '@/components/ui/nav/sidebar'
import { OfflineIndicator } from '@/components/ui/pwa/offline-indicator'
import { APP_CONFIG } from '@/lib/config/constants'
import { OnboardingProvider } from '@/lib/onboarding'
import { useNetworkStatus } from '@/lib/pwa'
import type { BreadcrumbDefinition } from '@/lib/router/breadcrumbs'
import { useBreadcrumbs } from '@/lib/router/use-breadcrumbs'
import { InstallBanner } from './install-banner'
import { TopBarActionsProvider, TopBarActionsSlot } from './top-bar-actions'

interface AppLayoutProps {
  children: ReactNode
  breadcrumbs?: BreadcrumbDefinition[]
}

function normalizeBreadcrumbs(items: BreadcrumbDefinition[]) {
  const normalized: BreadcrumbDefinition[] = []

  for (const item of items) {
    if (item.label === 'Home' && items.length > 1) continue

    const previous = normalized[normalized.length - 1]
    if (previous?.label === item.label && previous?.href === item.href) continue

    normalized.push(item)
  }

  return normalized
}

export function AppLayout({ children, breadcrumbs: initialBreadcrumbs }: AppLayoutProps) {
  const breadcrumbs = useBreadcrumbs(initialBreadcrumbs)
  const { isOnline } = useNetworkStatus()

  const shellBreadcrumbs = useMemo(() => normalizeBreadcrumbs(breadcrumbs), [breadcrumbs])

  const shellTitle = shellBreadcrumbs[shellBreadcrumbs.length - 1]?.label ?? 'Today'
  const shellSection =
    shellBreadcrumbs.length > 1 ? shellBreadcrumbs[shellBreadcrumbs.length - 2]?.label : undefined

  return (
    <OnboardingProvider>
      <SidebarProvider>
        <TopBarActionsProvider>
          <AppSidebar />
          <SidebarInset className="relative flex min-h-dvh flex-col overflow-hidden">
            <a href="#app-main-content" className="app-skip-link">
              Skip to content
            </a>
            <div className="app-ambient-spot -left-12 top-4 h-36 w-36 bg-primary/18" />
            <div className="app-ambient-spot right-0 top-0 h-28 w-28 bg-route/16" />
            <header className="app-shell-header sticky top-0 z-30 border-b border-border/60 backdrop-blur-2xl">
              <div className="grid min-h-[var(--app-topbar-height)] grid-cols-[auto_minmax(0,1fr)] items-center gap-3 px-4 py-3 md:grid-cols-[auto_minmax(0,1fr)_minmax(18rem,auto)] md:px-6">
                <SidebarTrigger className="-ml-1 rounded-[4px] border border-transparent transition-all hover:border-border/70 hover:bg-background/80 active:scale-[0.98]" />
                <div className="min-w-0 flex-1">
                  {shellSection ? (
                    <p className="app-section-label truncate">{shellSection}</p>
                  ) : null}
                  <h1 className="truncate text-xl font-semibold tracking-tight text-foreground md:text-2xl">
                    {shellTitle}
                  </h1>
                </div>
                <div className="col-span-2 min-w-0 md:col-span-1">
                  <TopBarActionsSlot />
                </div>
              </div>
            </header>

            <div className="empty:hidden flex flex-col gap-3 ">
              <InstallBanner />
              <OfflineIndicator isOnline={isOnline} hideDelay={APP_CONFIG.TOAST_DURATION} />
            </div>

            <main
              id="app-main-content"
              className="relative flex min-h-0 flex-1 flex-col gap-4 px-2 py-0"
              tabIndex={-1}
            >
              <div className="app-bg-grid pointer-events-none absolute inset-0 -z-10 opacity-28 [mask-image:linear-gradient(to_bottom,black,transparent_64%)]" />
              {children}
            </main>
          </SidebarInset>
        </TopBarActionsProvider>
      </SidebarProvider>
    </OnboardingProvider>
  )
}
